import { useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { CheckCircle, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import apolloClient from "@/lib/apollo-client";
import { getCookie } from "@/utils/cookies";
import { gql } from '@apollo/client';

// Define the GraphQL mutation
const UPDATE_ENROLLMENT_STATUS = gql`
  mutation UpdateEnrollment($input: UpdateEnrollmentInput!) {
    updateEnrollment(input: $input) {
      id
      userId
      trainingId
      status
      enrolledAt
    }
  }
`;

// Helper function to get badge class based on status
const getStatusBadgeClass = (status: string): string => {
  // Handle case where status might be undefined or null
  if (!status) return 'bg-gray-100 text-gray-800 border-gray-200';

  // Normalize status to uppercase for consistent comparison
  const normalizedStatus = status.toUpperCase();

  switch (normalizedStatus) {
    case 'APPROVED':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'COMPLETED':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'CANCELLED':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      console.log('Unknown status:', status);
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

interface EnrollmentStatusUpdaterProps {
  enrollmentId: number;
  initialStatus: string;
  onStatusUpdated?: (newStatus: string) => void;
}

const EnrollmentStatusUpdater = ({
  enrollmentId,
  initialStatus,
  onStatusUpdated
}: EnrollmentStatusUpdaterProps) => {
  const [status, setStatus] = useState(initialStatus || 'PENDING');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === status) return;

    setIsUpdating(true);

    try {
      // Immediately update the UI for better user experience
      setStatus(newStatus);

      // Get the token for authentication
      const token = getCookie('token');

      // Ensure the ID is a number
      const numericId = Number(enrollmentId);

      console.log(`Updating enrollment ${numericId} to status: ${newStatus}`);

      // Make the GraphQL mutation directly
      const { data } = await apolloClient.mutate({
        mutation: UPDATE_ENROLLMENT_STATUS,
        variables: {
          input: {
            id: numericId,
            status: newStatus
          }
        },
        context: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        },
        fetchPolicy: 'no-cache'
      });

      console.log('Enrollment update response:', data);

      if (!data || !data.updateEnrollment) {
        throw new Error('Failed to update enrollment status');
      }

      // Show success message
      toast({
        title: "Status Updated",
        description: `Enrollment status has been updated to ${newStatus}`,
        variant: "default"
      });

      // Show success indicator
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      // Call the callback if provided
      if (onStatusUpdated) {
        onStatusUpdated(newStatus);
      }
    } catch (error) {
      console.error('Error updating enrollment status:', error);

      // Revert to the original status
      setStatus(initialStatus);

      // Show error message
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update enrollment status",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3">
        <div className="relative">
          <select
            value={status.toUpperCase()}
            onChange={(e) => handleStatusChange(e.target.value)}
            className={`p-2 text-sm border rounded-md bg-background focus:ring-2 focus:ring-primary w-full pr-8 appearance-none text-black ${showSuccess ? 'border-green-500 ring-1 ring-green-500' : ''}`}
            disabled={isUpdating}
          >
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <Badge variant="outline" className={`${getStatusBadgeClass(status)} transition-all duration-300`}>
          {status}
        </Badge>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-green-500 flex-shrink-0"
          >
            <CheckCircle className="h-5 w-5" />
          </motion.div>
        )}
      </div>
      {isUpdating && (
        <div className="mt-1 flex items-center text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          Updating status...
        </div>
      )}
    </div>
  );
};

export default EnrollmentStatusUpdater;
